import { useState } from 'react';
import { ChevronDown, ChevronRight, Crown, Lock, Smartphone, Trash2 } from 'lucide-react';
import {
  MEMBER_ROLES,
  PERMISSION_GROUPS,
  PERMISSION_LABELS,
  ROLE_DEFAULTS,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  grantsBeyond,
  isMemberRole,
  oldAppStillOffers,
  overridesFromEdits,
  type MemberRole,
  type Permission,
} from '@mustauto/types/permissions';
import { formatApiError, maskPhone } from '@mustauto/utils';
import { cn, useConfirm, useToast } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import {
  useRemoveMember,
  useSetWorksOnCars,
  useTransferOwnership,
  useUpdateMember,
  type TeamMember,
  type TeamViewer,
} from '@/hooks/useTeamMembers';

const LOCKED_TEXT: Record<NonNullable<TeamMember['lockedReason']>, string> = {
  owner: 'Quem criou a oficina. Tem tudo, ninguém edita nem remove.',
  self: 'Você não muda o seu próprio papel nem as suas permissões.',
  no_permission: 'Você vê a equipe, mas não muda o que cada um faz.',
  outranks: 'Para mexer num gestor é preciso ter tudo o que o gestor tem.',
};

/**
 * O app da pessoa é anterior às permissões (D8): ele só conhece o perfil da
 * conta e não respeita o que o gestor tirou. O servidor barra, sem explicar,
 * e o valor escondido aparece como R$ 0,00. A lista diz o que o perfil da
 * conta oferece e a pessoa não pode — não promete que cada item está numa
 * tela do app antigo (movimentar estoque nunca esteve na do mecânico).
 */
function OutdatedAppNote({ member, firstName }: { member: TeamMember; firstName: string }) {
  const offered = oldAppStillOffers({ role: member.role, isOwner: member.isOwner, overrides: member.overrides });
  const shown = offered.slice(0, 3).map((p) => PERMISSION_LABELS[p].toLowerCase());
  const more = offered.length - shown.length;
  return (
    <div
      role="note"
      className={cn(
        'flex items-start gap-2 rounded-lg px-3 py-2 text-sm',
        offered.length > 0 ? 'bg-warning/10 text-foreground' : 'bg-muted text-muted-foreground',
      )}
    >
      <Smartphone size={14} className="mt-0.5 shrink-0" />
      <div className="space-y-1">
        <p className="font-medium">O app de {firstName} está desatualizado</p>
        {offered.length > 0 ? (
          <p>
            Em algum celular usado por {firstName} nas últimas duas semanas, o app ainda não respeita o que você tirou
            ({shown.join('; ')}{more > 0 ? ` e mais ${more}` : ''}). Se {firstName} tentar, o MustAuto não deixa, mas o
            app não explica o porquê, e onde você escondeu valores aparece R$ 0,00. Peça para {firstName} atualizar o
            app.
          </p>
        ) : (
          <p>
            Hoje não faz diferença. Se você tirar alguma permissão, o app de {firstName} não vai respeitar até ser
            atualizado.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * "Também trabalha nos carros" (perfil único, 5a): vem primeiro na escolha do
 * responsável, recebe os avisos "aos mecânicos" e vê "Minhas OS" no app do
 * gestor. Não concede permissão. A própria pessoa sempre liga a dela — o dono
 * também, que ninguém mais edita; o Mecânico trabalha nos carros sempre.
 */
function WorksOnCarsRow({ member, viewer, firstName }: { member: TeamMember; viewer: TeamViewer; firstName: string }) {
  const { toast } = useToast();
  const setWorksOnCars = useSetWorksOnCars();
  const isSelf = member.userId === viewer.userId;
  const isMechanic = member.role === 'mechanic';
  const canChange = !isMechanic && (isSelf || member.lockedReason === null);
  const hint = isMechanic
    ? 'O Mecânico sempre trabalha nos carros.'
    : `${isSelf ? 'Você aparece' : `${firstName} aparece`} primeiro na escolha do responsável pela OS, recebe os avisos da oficina para os mecânicos e vê "Minhas OS" no app.`;

  async function change(value: boolean) {
    try {
      await setWorksOnCars.mutateAsync({ userId: member.userId, worksOnCars: value });
      toast({ type: 'success', title: 'Salvo' });
    } catch (err) {
      toast({ type: 'error', title: 'Não foi possível salvar', description: formatApiError(err, 'Tente de novo.') });
    }
  }

  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-border px-3 py-2.5">
      <div className="min-w-0 text-sm">
        <span className="font-medium">{isSelf ? 'Também trabalho nos carros' : 'Também trabalha nos carros'}</span>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <input
        type="checkbox"
        role="switch"
        aria-label={isSelf ? 'Também trabalho nos carros' : 'Também trabalha nos carros'}
        checked={member.worksOnCars}
        disabled={!canChange || setWorksOnCars.isPending}
        onChange={(e) => void change(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-input accent-primary disabled:opacity-50"
      />
    </div>
  );
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1]![0] : '')).toUpperCase() || '?';
}

/**
 * A ficha da pessoa (docs/ref/preview_permissoes.html, "A ficha da pessoa"):
 * papel em cima, os grupos fechados com o resumo "6 de 8", e cada interruptor
 * em um de três estados — o padrão do papel, alterado, ou travado porque quem
 * lê não tem aquela permissão para liberar (regra 4 do D7). Cada interruptor
 * salva na hora e vai ao histórico.
 */
export function PersonSheet({ member, viewer }: { member: TeamMember; viewer: TeamViewer }) {
  const { toast } = useToast();
  const confirm = useConfirm();
  const update = useUpdateMember();
  const remove = useRemoveMember();
  const transfer = useTransferOwnership();
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());

  const role: MemberRole = isMemberRole(member.role) ? member.role : 'mechanic';
  const viewerPerms = new Set<Permission>(viewer.permissions);
  const has = new Set<Permission>(member.permissions);
  const editable = member.lockedReason === null;
  const adjustments = Object.keys(member.overrides).length;
  const firstName = member.name.split(' ')[0] ?? member.name;
  const busy = update.isPending || remove.isPending || transfer.isPending;

  // Até o gestor deixar de decidir pelo papel em toda rota, ser gestor é ter
  // tudo o que o gestor tem — é o que a API usa na regra 4.
  const before = role === 'manager' ? ROLE_DEFAULTS.manager : has;

  async function save(change: { role?: MemberRole; overrides?: Record<string, boolean> }, success: string) {
    try {
      await update.mutateAsync({ userId: member.userId, ...change });
      toast({ type: 'success', title: success });
    } catch (err) {
      toast({ type: 'error', title: 'Não foi possível salvar', description: formatApiError(err, 'Tente de novo.') });
    }
  }

  function toggle(p: Permission, on: boolean) {
    const desired = new Set(has);
    if (on) desired.add(p);
    else desired.delete(p);
    void save({ overrides: overridesFromEdits(role, desired) }, 'Salvo');
  }

  async function changeRole(next: MemberRole) {
    if (next === role) return;
    if (adjustments > 0) {
      const ok = await confirm({
        title: `Mudar ${firstName} para ${ROLE_LABELS[next]}?`,
        description: `Os ${adjustments === 1 ? 'ajuste feito' : `${adjustments} ajustes feitos`} para ${firstName} saem, e ${firstName} passa a ter o padrão de ${ROLE_LABELS[next]}.`,
        confirmLabel: 'Mudar o papel',
      });
      if (!ok) return;
    }
    await save({ role: next }, `${firstName} agora é ${ROLE_LABELS[next]}`);
  }

  async function backToDefault() {
    const ok = await confirm({
      title: `Voltar ${firstName} ao padrão de ${ROLE_LABELS[role]}?`,
      description: 'Os ajustes feitos para esta pessoa saem.',
      confirmLabel: 'Voltar ao padrão',
    });
    if (ok) await save({ overrides: {} }, 'Voltou ao padrão do papel');
  }

  async function removeFromShop() {
    const ok = await confirm({
      title: `Remover ${firstName} da oficina?`,
      description: `${firstName} perde o acesso a esta oficina na hora. As OS que fez continuam no histórico.`,
      confirmLabel: 'Remover',
      dangerous: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(member.userId);
      toast({ type: 'success', title: `${firstName} saiu da oficina` });
    } catch (err) {
      toast({ type: 'error', title: 'Não foi possível remover', description: formatApiError(err, 'Tente de novo.') });
    }
  }

  async function passOwnership() {
    const ok = await confirm({
      title: `Passar a posse da oficina para ${firstName}?`,
      description: `${firstName} vira o dono: tem tudo e ninguém edita. Você continua como gestor.`,
      confirmLabel: 'Passar a posse',
      dangerous: true,
    });
    if (!ok) return;
    try {
      await transfer.mutateAsync(member.userId);
      toast({ type: 'success', title: `${firstName} é o novo dono` });
    } catch (err) {
      toast({ type: 'error', title: 'Não foi possível passar a posse', description: formatApiError(err, 'Tente de novo.') });
    }
  }

  const canRemove =
    !member.isOwner &&
    member.userId !== viewer.userId &&
    viewerPerms.has('equipe.convidar') &&
    (role !== 'manager' || [...ROLE_DEFAULTS.manager].every((p) => viewerPerms.has(p)));
  const canPassOwnership = viewer.isOwner && role === 'manager' && !member.isOwner;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Avatar member={member} size="lg" />
        <div className="min-w-0">
          <p className="truncate text-lg font-semibold">{member.name}</p>
          <p className="text-xs text-muted-foreground">
            {member.phone ? `${maskPhone(member.phone)} · ` : ''}na equipe desde {new Date(member.joinedAt).toLocaleDateString('pt-BR')}
          </p>
        </div>
      </div>

      {member.outdatedApp && !member.isOwner && <OutdatedAppNote member={member} firstName={firstName} />}

      {member.isOwner ? (
        <div className="space-y-3 rounded-lg border border-border p-4">
          <p className="flex items-center gap-2 text-sm font-semibold">
            <Crown size={16} /> Dono
          </p>
          <p className="text-sm text-muted-foreground">{LOCKED_TEXT.owner}</p>
        </div>
      ) : (
        <div className="space-y-2">
          <label htmlFor="member-role" className="text-sm font-medium">Papel</label>
          <select
            id="member-role"
            value={role}
            disabled={!editable || busy}
            onChange={(e) => void changeRole(e.target.value as MemberRole)}
            className="w-full rounded-lg border border-border border-input bg-background px-3 py-2 text-sm disabled:opacity-60"
          >
            {MEMBER_ROLES.map((r) => {
              const after = r === 'manager' ? ROLE_DEFAULTS.manager : ROLE_DEFAULTS[r];
              const blocked = r !== role && grantsBeyond(viewerPerms, before, after).length > 0;
              return (
                <option key={r} value={r} disabled={blocked}>
                  {ROLE_LABELS[r]}{blocked ? ' — você não pode liberar tudo o que este papel tem' : ''}
                </option>
              );
            })}
          </select>
          <p className="text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[role]}</p>
          {(role === 'reception' || role === 'finance') && (
            <p className="text-xs text-muted-foreground">
              No app, {firstName} abre o painel de gestão, só com o que este papel permite (no app desatualizado, o perfil de mecânico). O que você desligar aqui vale na hora, no app e no painel.
            </p>
          )}
        </div>
      )}

      <WorksOnCarsRow member={member} viewer={viewer} firstName={firstName} />

      {!editable && !member.isOwner && member.lockedReason && (
        <p className="flex items-start gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
          <Lock size={14} className="mt-0.5 shrink-0" />
          {LOCKED_TEXT[member.lockedReason]}
        </p>
      )}

      {!member.isOwner && (
        <div className="space-y-2">
          <p className="text-sm font-medium">O que {firstName} pode fazer</p>
          <div className="divide-y divide-border rounded-lg border border-border">
            {PERMISSION_GROUPS.map((group) => {
              const keys = group.permissions.map(([key]) => key as Permission);
              const count = keys.filter((p) => has.has(p)).length;
              const altered = keys.some((p) => p in member.overrides);
              const open = openGroups.has(group.key);
              return (
                <div key={group.key}>
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() =>
                      setOpenGroups((prev) => {
                        const next = new Set(prev);
                        if (next.has(group.key)) next.delete(group.key);
                        else next.add(group.key);
                        return next;
                      })
                    }
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm hover:bg-muted/50"
                  >
                    <span className="flex items-center gap-2 font-medium">
                      {group.label}
                      {altered && <Badge variant="warning">alterado</Badge>}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-muted-foreground">
                      {count} de {keys.length}
                      {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>
                  </button>
                  {open && (
                    <ul className="space-y-1 px-4 pb-3">
                      {group.permissions.map(([key, label]) => {
                        const p = key as Permission;
                        const checked = has.has(p);
                        const cantGrant = !checked && !viewerPerms.has(p);
                        const disabled = !editable || busy || cantGrant;
                        return (
                          <li key={p} className="flex items-start justify-between gap-3 py-1.5">
                            <div className="min-w-0 text-sm">
                              <span className={cn(disabled && 'text-muted-foreground')}>{label}</span>
                              {p in member.overrides && <span className="ml-2 text-xs font-medium text-warning">alterado</span>}
                              {editable && cantGrant && (
                                <p className="text-xs text-muted-foreground">Você não tem esta permissão para liberar</p>
                              )}
                            </div>
                            <input
                              type="checkbox"
                              role="switch"
                              aria-label={label}
                              checked={checked}
                              disabled={disabled}
                              onChange={(e) => toggle(p, e.target.checked)}
                              className="mt-0.5 h-4 w-4 shrink-0 rounded border-input accent-primary disabled:opacity-50"
                            />
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-t border-border pt-4">
        {editable && adjustments > 0 && grantsBeyond(viewerPerms, has, ROLE_DEFAULTS[role]).length === 0 && (
          <button
            type="button"
            onClick={() => void backToDefault()}
            disabled={busy}
            className="rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            Voltar ao padrão de {ROLE_LABELS[role]}
          </button>
        )}
        {canPassOwnership && (
          <button
            type="button"
            onClick={() => void passOwnership()}
            disabled={busy}
            className="flex items-center gap-1 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            <Crown size={14} /> Passar a posse
          </button>
        )}
        {canRemove && (
          <button
            type="button"
            onClick={() => void removeFromShop()}
            disabled={busy}
            className="flex items-center gap-1 rounded-lg border border-border border-destructive/40 px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
          >
            <Trash2 size={14} /> Remover da oficina
          </button>
        )}
      </div>
    </div>
  );
}

export function Avatar({ member, size = 'md' }: { member: Pick<TeamMember, 'name' | 'avatarUrl'>; size?: 'md' | 'lg' }) {
  const box = size === 'lg' ? 'h-12 w-12 text-base' : 'h-9 w-9 text-xs';
  if (member.avatarUrl) {
    return <img src={member.avatarUrl} alt="" className={cn('shrink-0 rounded-full object-cover', box)} />;
  }
  return (
    <div className={cn('flex shrink-0 items-center justify-center rounded-full bg-muted font-semibold text-foreground', box)}>
      {initials(member.name)}
    </div>
  );
}

